import { format, parse, compareAsc, differenceInMonths } from 'date-fns';
import { LeaderboardData, Puzzle, SolverStats } from '../features/leaderboard/types';
import { calculateRisingStars } from './risingStars';

export const MONTH_CODES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const FULL_TO_SHORT = new Map(
  ['January', 'February', 'March', 'April', 'May', 'June',
   'July', 'August', 'September', 'October', 'November', 'December']
    .map((full, i) => [full, MONTH_CODES[i]])
);

/**
 * Per-puzzle placement for a solver. `rank` is the 1-based position in the
 * source JSON `solvers[]` array, which Jane Street publishes in submission
 * order. `percentile` is 100 × (1 − (rank − 1) / (total − 1)) — higher is
 * better. It is `null` when total < 2, since a single-solver puzzle has
 * no meaningful percentile.
 */
export interface SolverPlacement {
  puzzle: Puzzle;
  rank: number;
  total: number;
  percentile: number | null;
}

/**
 * Find the solver's 1-based placement inside `puzzle.solvers`, or `null`
 * if they are not in the list. Placement is derived solely from JSON
 * order — never from name, date, or any other sort.
 */
export const findSolverPlacement = (
  solverName: string,
  puzzle: Puzzle,
): SolverPlacement | null => {
  const solvers = puzzle.solvers;
  if (!solvers || solvers.length === 0) return null;
  const idx = solvers.indexOf(solverName);
  if (idx < 0) return null;
  const total = solvers.length;
  const rank = idx + 1;
  const percentile = total > 1 ? 100 * (1 - (rank - 1) / (total - 1)) : null;
  return { puzzle, rank, total, percentile };
};

/**
 * Mean of percentile ranks across the solver's puzzles. Puzzles with
 * total < 2 are excluded (no defined percentile). Returns `null` if no
 * puzzle in the input qualifies.
 *
 * Formula: 100 × (1 − (rank − 1) / (total − 1)), averaged. Higher is
 * better; 100 = always first, 0 = always last.
 */
export const computeAveragePercentile = (
  placements: SolverPlacement[],
): { value: number; sampleSize: number } | null => {
  const usable = placements.filter((p) => p.percentile !== null);
  if (usable.length === 0) return null;
  const sum = usable.reduce((acc, p) => acc + (p.percentile as number), 0);
  return { value: sum / usable.length, sampleSize: usable.length };
};

/**
 * 1224-style ranks by solve count: tied counts share a rank, so a
 * solver's rank only moves when the set of solvers above them changes.
 */
export const competitionRanks = (
  counts: Map<string, number>,
): Map<string, number> => {
  const ranks = new Map<string, number>();
  const ordered = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  let rank = 0;
  let prevCount: number | null = null;
  ordered.forEach(([name, count], index) => {
    if (count !== prevCount) {
      rank = index + 1;
      prevCount = count;
    }
    ranks.set(name, rank);
  });
  return ranks;
};

/**
 * Rank movement vs. the standings before the newest puzzle month.
 * Positive = moved up, negative = moved down, `null` = not ranked last
 * month (first solve happened this month). Competition ranks are used on
 * both snapshots so ties never produce noise from arbitrary ordering.
 */
export const computeRankChanges = (
  puzzles: Puzzle[],
): Map<string, number | null> => {
  const changes = new Map<string, number | null>();
  if (!puzzles || puzzles.length === 0) return changes;

  const monthKey = (p: Puzzle): number => {
    const date = parseDate(p.date_text);
    return date.getFullYear() * 12 + date.getMonth();
  };
  const latestMonth = Math.max(...puzzles.map(monthKey));

  const currentCounts = new Map<string, number>();
  const previousCounts = new Map<string, number>();
  puzzles.forEach((p) => {
    const isLatestMonth = monthKey(p) === latestMonth;
    Array.from(new Set(p.solvers || [])).forEach((name) => {
      currentCounts.set(name, (currentCounts.get(name) || 0) + 1);
      if (!isLatestMonth) {
        previousCounts.set(name, (previousCounts.get(name) || 0) + 1);
      }
    });
  });

  const currentRanks = competitionRanks(currentCounts);
  const previousRanks = competitionRanks(previousCounts);
  currentRanks.forEach((currRank, name) => {
    const prevRank = previousRanks.get(name);
    changes.set(name, prevRank === undefined ? null : prevRank - currRank);
  });
  return changes;
};

/** Convert "January 2025" → "Jan 2025". Already-short dates pass through. */
export const formatDate = (dateText: string): string => {
  if (!dateText) return 'N/A';
  if (/^[A-Za-z]{3} \d{4}$/.test(dateText)) return dateText;
  const parts = dateText.split(' ');
  if (parts.length === 2) {
    const short = FULL_TO_SHORT.get(parts[0]);
    if (short) return `${short} ${parts[1]}`;
  }
  return dateText;
};

// Parse date from format like "March 2025"
const parseDate = (dateText: string): Date => {
  try {
    return parse(dateText, 'MMMM yyyy', new Date());
  } catch {
    return new Date();
  }
};

// Format date to "MMM yyyy"
const formatMonthYear = (date: Date): string => format(date, 'MMM yyyy');

/**
 * Calculate leaderboard statistics from raw puzzle data.
 * This is a fallback for when pre-computed stats.json is unavailable.
 */
export const calculateLeaderboardData = (puzzles: Puzzle[]): LeaderboardData => {
  if (!puzzles || puzzles.length === 0) {
    throw new Error('No puzzle data provided for leaderboard calculation');
  }

  const solverMap = new Map<string, SolverStats>();
  const allMonths = new Set<string>();
  
  // Sort puzzles by date (newest first) - only once
  const sortedPuzzles = [...puzzles].sort((a, b) => {
    const dateA = parseDate(a.date_text);
    const dateB = parseDate(b.date_text);
    return compareAsc(dateB, dateA);
  });
  
  // Process each puzzle
  sortedPuzzles.forEach(puzzle => {
    const puzzleDate = parseDate(puzzle.date_text);
    const monthKey = formatMonthYear(puzzleDate);
    allMonths.add(monthKey);
    
    // Skip empty solver arrays to improve performance
    if (!puzzle.solvers || puzzle.solvers.length === 0) return;

    // A name listed more than once on one puzzle (e.g. double puzzles)
    // still counts as one solve of that puzzle.
    Array.from(new Set(puzzle.solvers)).forEach(solverName => {
      if (!solverMap.has(solverName)) {
        solverMap.set(solverName, {
          name: solverName,
          puzzlesSolved: 0,
          firstAppearance: puzzle.date_text,
          lastSolve: puzzle.date_text,
          monthlyActivity: {},
          streaks: []
        });
      }
      
      const solver = solverMap.get(solverName)!;
      solver.puzzlesSolved += 1;
      solver.monthlyActivity[monthKey] = true;
      
      // Update first appearance if this puzzle is older
      if (compareAsc(puzzleDate, parseDate(solver.firstAppearance)) < 0) {
        solver.firstAppearance = puzzle.date_text;
      }

      // Update last solve if this puzzle is newer
      if (compareAsc(puzzleDate, parseDate(solver.lastSolve)) > 0) {
        solver.lastSolve = puzzle.date_text;
      }
    });
  });
  
  // Calculate streaks only for top solvers to improve performance
  const topSolversByCount = Array.from(solverMap.values())
    .sort((a, b) => b.puzzlesSolved - a.puzzlesSolved);
  
  // Calculate streaks for each top solver
  topSolversByCount.forEach(solver => {
    const months = Object.keys(solver.monthlyActivity).sort((a, b) => 
      compareAsc(parse(a, 'MMM yyyy', new Date()), parse(b, 'MMM yyyy', new Date()))
    );
    
    if (months.length === 0) return;
    
    let currentStreak = { 
      start: months[0], 
      end: months[0], 
      length: 1 
    };
    
    for (let i = 1; i < months.length; i++) {
      const prevDate = parse(months[i-1], 'MMM yyyy', new Date());
      const currDate = parse(months[i], 'MMM yyyy', new Date());
      
      // Check if months are consecutive
      if (differenceInMonths(currDate, prevDate) === 1) {
        currentStreak.end = months[i];
        currentStreak.length += 1;
      } else {
        // Save the completed streak if it's at least 2 months
        if (currentStreak.length >= 2) {
          solver.streaks.push({ ...currentStreak });
        }
        // Start a new streak
        currentStreak = { 
          start: months[i], 
          end: months[i], 
          length: 1 
        };
      }
    }
    
    // Add the last streak if it's at least 2 months
    if (currentStreak.length >= 2) {
      solver.streaks.push({ ...currentStreak });
    }
    
    // Sort streaks by length (longest first)
    solver.streaks.sort((a, b) => b.length - a.length);
  });
  
  // Get all solvers by puzzles solved, mapped to output shape
  const rankChanges = computeRankChanges(puzzles);
  const topSolvers = topSolversByCount.map(s => ({
    name: s.name,
    puzzlesSolved: s.puzzlesSolved,
    firstAppearance: formatMonthYear(parseDate(s.firstAppearance)),
    lastSolve: formatMonthYear(parseDate(s.lastSolve)),
    rankChange: rankChanges.get(s.name) ?? null,
  }));
  
  // Get solvers with longest streaks
  const longestStreaks = topSolversByCount
    .filter(solver => solver.streaks.length > 0)
    .map(solver => ({
      name: solver.name,
      streakLength: solver.streaks[0].length,
      startDate: formatMonthYear(parse(solver.streaks[0].start, 'MMM yyyy', new Date())),
      endDate: formatMonthYear(parse(solver.streaks[0].end, 'MMM yyyy', new Date())),
    }))
    .sort((a, b) => b.streakLength - a.streakLength)
    .slice(0, 20);
  
  const { risingStars, risingStarsAsOf } = calculateRisingStars(puzzles);
  
  // Calculate monthly participation - sample every other month for better performance
  const sortedMonths = Array.from(allMonths).sort((a, b) => 
    compareAsc(parse(a, 'MMM yyyy', new Date()), parse(b, 'MMM yyyy', new Date()))
  );
  
  // Sample months to reduce data points for monthly participation
  const sampledMonths = sortedMonths.length > 48 
    ? sortedMonths.filter((_, index) => index % 3 === 0 || index === sortedMonths.length - 1)
    : sortedMonths;
  
  const monthlyParticipation = sampledMonths.map(month => {
    const solversCount = Array.from(solverMap.values())
      .filter(solver => solver.monthlyActivity[month])
      .length;
    
    return {
      month,
      solvers: solversCount
    };
  });
  
  // Calculate solvers growth over time - include every month for complete visualization
  const newSolversByMonth = new Map<string, number>();
  solverMap.forEach(solver => {
    const firstMonth = formatMonthYear(parseDate(solver.firstAppearance));
    newSolversByMonth.set(firstMonth, (newSolversByMonth.get(firstMonth) || 0) + 1);
  });

  const solversGrowth: { month: string; totalSolvers: number }[] = [];
  let totalSolvers = 0;
  sortedMonths.forEach(month => {
    totalSolvers += newSolversByMonth.get(month) || 0;
    solversGrowth.push({ month, totalSolvers });
  });
  
  // Ensure the last data point shows the total number of unique solvers
  if (solversGrowth.length > 0) {
    const lastPoint = solversGrowth[solversGrowth.length - 1];
    if (lastPoint.totalSolvers !== solverMap.size) {
      lastPoint.totalSolvers = solverMap.size;
    }
  }
  
  // Calculate most solved puzzles
  const mostSolvedPuzzles = sortedPuzzles
    .map(puzzle => {
      // Extract year and month from date_text (e.g., "January 2023")
      const date = parseDate(puzzle.date_text);
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // JavaScript months are 0-indexed
      
      return {
        id: `${year}-${month}`,
        name: puzzle.name,
        solvers: new Set(puzzle.solvers || []).size,
        solution_url: puzzle.solution_url
      };
    })
    .sort((a, b) => b.solvers - a.solvers)
    .slice(0, 20); // Get top 20 to have some buffer

  // Pre-compute solver distribution for the chart
  const solvers = Array.from(solverMap.values());
  const solverDistribution = {
    onePuzzle: solvers.filter(s => s.puzzlesSolved === 1).length,
    twoToNine: solvers.filter(s => s.puzzlesSolved >= 2 && s.puzzlesSolved <= 9).length,
    tenPlus: solvers.filter(s => s.puzzlesSolved >= 10).length,
  };

  return {
    totalPuzzles: puzzles.length,
    uniqueSolvers: solverMap.size,
    solverDistribution,
    topSolvers,
    longestStreaks,
    risingStars,
    risingStarsAsOf,
    monthlyParticipation,
    solversGrowth,
    mostSolvedPuzzles,
  };
};
